// Right-side slide-in drawer that wraps the existing ChatPage. The drawer
// owns the session lifecycle at a thin level:
//
//   - On first open for the current `projectId`, it calls `POST /sessions`
//     once and caches the resulting `sessionId` until the project changes
//     (an unmount, since the DashboardPage key changes too).
//   - Subsequent opens reuse the same session — the inner ChatPage and its
//     WS persist across close+open (CSS-only collapse).
//   - The Conversations accordion can swap the active session via
//     `setActiveSessionId(thread.sessionId)`; ChatPage rehydrates that
//     session using its existing reconnect flow.
//
// The drawer itself is never unmounted while the dashboard route stays on
// the same project — the design treats chat as a continuously-present panel.

import { useCallback, useEffect, useState, type JSX } from "react";

import { ChatPage } from "../../chat/ChatPage";
import { useT } from "../../i18n/index.js";
import { createSession } from "../../projects/useProjects";

import { Icon } from "./Icon";

interface ChatDrawerProps {
  readonly projectId: number;
  readonly open: boolean;
  readonly seed: string | null;
  /** The user-authored span of a machine-composed `seed` — threaded straight
   *  through to ChatPage. Only meaningful with `seedMode: "send"`; the drawer's
   *  own callers use "draft", where the user sends the text themselves. */
  readonly seedEvidence?: string;
  readonly onSeedConsumed: () => void;
  /**
   * "draft" (default) lets the user review/edit before sending. "send"
   * fires the seed as a user_message the moment the chat is ready, used
   * by the post-create refinement flow where the seed is a system-shaped
   * "please refine issue #N" prompt the user shouldn't have to retrigger.
   */
  readonly seedMode?: "draft" | "send";
  readonly onClose: () => void;
  /** Set by the Conversations accordion to rehydrate a specific session. */
  readonly activeSessionId: string | null;
  readonly setActiveSessionId: (id: string) => void;
  /**
   * Set by DashboardPage while a pre-warm createSession is in flight
   * (fired when the New Request form opens). When true, the drawer's
   * own create-on-open path waits — without this gate, a user who
   * submits the form before pre-warm completes races two
   * createSession calls and the slower one's session is orphaned.
   */
  readonly prewarming?: boolean;
  readonly onBackToPicker: () => void;
}

export const ChatDrawer = ({
  projectId,
  open,
  seed,
  seedEvidence,
  onSeedConsumed,
  seedMode = "draft",
  onClose,
  activeSessionId,
  setActiveSessionId,
  prewarming = false,
  onBackToPicker,
}: ChatDrawerProps): JSX.Element => {
  const t = useT();
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Open with no session in flight → mint one. The drawer renders a
  // boot-pill chrome until ChatPage takes over. `prewarming` defers
  // this create when the parent already kicked off a pre-warm — without
  // it, a user submitting the New Request form fast enough races two
  // createSession calls.
  useEffect(() => {
    if (!open) return;
    if (activeSessionId !== null) return;
    if (creating) return;
    if (prewarming) return;
    setCreating(true);
    setCreateError(null);
    createSession(projectId)
      .then(({ sessionId }) => {
        setActiveSessionId(sessionId);
      })
      .catch((err: unknown) => {
        setCreateError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setCreating(false));
  }, [open, projectId, activeSessionId, creating, prewarming, setActiveSessionId]);

  // Esc closes when no input is focused. ⌘K toggles from the DashboardPage
  // shell so the dashboard owns the global shortcut.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const target = e.target as Element | null;
      if (target && target.tagName === "TEXTAREA") return;
      if (target && target.tagName === "INPUT") return;
      if (target && target.tagName === "SELECT") return;
      if (target instanceof HTMLElement && target.isContentEditable) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const onPickSession = useCallback(
    (id: string) => {
      setActiveSessionId(id);
    },
    [setActiveSessionId],
  );

  return (
    <aside
      className={`chat-drawer ${open ? "open" : ""}`}
      aria-hidden={!open}
      data-testid="chat-drawer"
    >
      <div className="chat-drawer-head">
        <span className="chat-drawer-title">{t("dashboard.drawer.askLoom")}</span>
        <button
          type="button"
          className="chat-drawer-close"
          onClick={onClose}
          title={t("dashboard.drawer.closeTitle")}
          aria-label={t("dashboard.drawer.closeAria")}
        >
          <Icon name="x" size={14} />
        </button>
      </div>
      <div className="chat-drawer-body">
        {createError !== null && (
          <div className="chat-drawer-error">{createError}</div>
        )}
        {activeSessionId === null ? (
          <div className="chat-drawer-boot" aria-live="polite">
            {creating ? t("dashboard.drawer.starting") : t("dashboard.drawer.preparing")}
          </div>
        ) : (
          <ChatPage
            sessionId={activeSessionId}
            onPickSession={onPickSession}
            onBackToPicker={onBackToPicker}
            embedded
            seed={seed}
            seedEvidence={seedEvidence}
            onSeedConsumed={onSeedConsumed}
            seedMode={seedMode}
          />
        )}
      </div>
    </aside>
  );
};
